<template>
  <v-row>
    <v-col>
      <v-card>
        <v-toolbar :dark="$vuetify.theme.dark" color="primary">
          <v-toolbar-title>
            {{ $t('Мастер создания запросов на регистрацию организации') }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-row>
            <v-col>
              <request-result-info
                  :process-comment="request.processComment"
                  :request-status="request.status" />
            </v-col>
          </v-row>
          <v-row v-if="draft.organizationId">
            <v-col class="text-right">
              <btn-icon-tip
                  v-if="draft.organizationId"
                  :id="`getRegCard-${draft.organizationId}`"
                  icon="description"
                  :tip="$t('Скачать регистрационную карточку')"
                  @click="getRegistrationCard" />
            </v-col>
          </v-row>
          <custom-stepper
              v-model="stepNumber"
              :steps-param-array="stepsProperties"
              :loading="isLoading"
              :disabled-forward-btn="isLoading"
              :success="success">

            <template v-slot:main-1>
              <organization-info
                  ref="organizationInfo"
                  :organization-info="draft"
                  :readonly="readonly"
                  :participant-types="PARTICIPANT_TYPE_EDIT_ENUM"
                  @change="mergeFieldsAction" />
            </template>

            <template v-slot:main-2>
              <bank-accounts-list
                  ref="bankAccounts"
                  :bank-accounts="bankAccounts"
                  :can-create="!readonly"
                  :can-delete="!readonly"
                  :loading="isLoading"
                  :create-bank-account="createBankAccount"
                  @change="getBankAccountsDrafts"
                  @deleteBankAccount="deleteBankAccount" />
            </template>

            <template v-slot:main-3>
              <v-row>
                <v-col>
                  <identity-document-list
                      ref="requestFiles"
                      :request-files="requestFiles"
                      :document-types="requiredDocumentTypes"
                      :readonly="readonly"
                      :loading="isLoading"
                      @uploadFile="uploadFile" />
                </v-col>
              </v-row>
            </template>

            <template v-slot:main-4>
              <v-row>
                <v-col>
                  <organization-info-view :organization-info="draft">
                    <template v-slot:additional>
                      <v-expansion-panel v-test="{ panel: '' }">
                        <v-expansion-panel-header>
                          <div id="account_balance">
                            <v-icon>
                              account_balance
                            </v-icon>
                            {{ $t('Банковские реквизиты') }}
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content eager>
                          <bank-accounts-list
                              :bank-accounts="bankAccounts"
                              :loading="isLoading" />
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                      <v-expansion-panel v-test="{ panel: '' }">
                        <v-expansion-panel-header>
                          <div id="attach_file">
                            <v-icon>
                              attach_file
                            </v-icon>
                            {{ $t('Прикрепленные документы') }}
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content eager>
                          <identity-document-list-view
                              :request-files="requestFiles"
                              :document-types="requiredDocumentTypes"
                              :loading="isLoading" />
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </template>
                  </organization-info-view>
                </v-col>
              </v-row>
            </template>
            <template v-slot:buttons-4>
              <v-btn
                  v-if="canSendRequest && !readonly"
                  id="sendBtn"
                  color="primary"
                  :disabled="isLoading"
                  @click="sendOrganizationRegistrationRequest">
                {{ $t('Отправить') }}
              </v-btn>
            </template>
          </custom-stepper>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
  import Vue from 'vue'
  import CustomStepper from '@/components/reused/ui/CustomStepper.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import OrganizationInfo from '@/components/reused/organization/OrganizationInfo.vue'
  import OrganizationInfoView from '@/components/reused/organization/OrganizationInfoView.vue'
  import IdentityDocumentList from '@/components/reused/files/IdentityDocumentList.vue'
  import BankAccountsList from '@/components/reused/bankAccounts/BankAccountsList.vue'
  import IdentityDocumentListView from '@/components/reused/files/IdentityDocumentListView.vue'
  import RequestResultInfo from './RequestResultInfo.vue'
  import { ORG_REQUEST_STATUSES } from '@/constants/business'
  import { PARTICIPANT_TYPE_EDIT_ENUM } from '@/constants/enums'
  import organizationMixin from '@/utils/mixins/scoped/organizationMixin.js'
  import { mapActions, mapState } from 'vuex'
  import { ORGANIZATION_DRAFT, IDENTITY_FILES, BANK_ACCOUNTS_DRAFTS } from '@/constants/store.js'

  export default {
    components: {
      CustomStepper,
      OrganizationInfo,
      OrganizationInfoView,
      RequestResultInfo,
      IdentityDocumentList,
      IdentityDocumentListView,
      BtnIconTip,
      BankAccountsList
    },
    mixins: [organizationMixin],
    data () {
      return {
        PARTICIPANT_TYPE_EDIT_ENUM,
        stepNumber: 1,
        isLoading: false,
        success: false
      }
    },
    computed: {
      ...mapState(ORGANIZATION_DRAFT.NAMESPACE, {
        draft: state => (state[ORGANIZATION_DRAFT.STATE.DRAFT] || { }),
        request: state => (state[ORGANIZATION_DRAFT.STATE.REQUEST] || {}),
        draftChanged: state => (state[ORGANIZATION_DRAFT.STATE.DRAFT_CHANGED] || false)
      }),
      ...mapState(IDENTITY_FILES.NAMESPACE, {
        requestFiles: state => (state[IDENTITY_FILES.STATE.REQUEST_FILES] || []),
        requiredDocumentTypes: state => (state[IDENTITY_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      ...mapState(BANK_ACCOUNTS_DRAFTS.NAMESPACE, {
        bankAccounts: state => (state[BANK_ACCOUNTS_DRAFTS.STATE.BANK_ACCOUNTS] || [])
      }),
      requestType () {
        return this.getRequestTypeByOrganization(
          this.draft.organizationType,
          this.draft.participantType,
          this.draft.isEIO)
      },
      stepsProperties () {
        return [
          {
            stepNumber: 1,
            stepTitle: this.$t('Добавление информации об организации'),
            stepTip: this.$t('Введение регистрационной информации'),
            successText: this.$t('Изменения сохранены'),
            toNextStep: this.validateAndSaveOrganization,
            toPrevStep: null,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 2,
            stepTitle: this.$t('Добавление банковских счетов'),
            stepTip: '',
            successText: '',
            toNextStep: this.validateBankAccounts,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 3,
            stepTitle: this.$t('Добавление документов'),
            stepTip: '',
            successText: '',
            toNextStep: this.validateAndSyncRequestFiles,
            toPrevStep: this.syncRequestFiles,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 4,
            stepTitle: this.$t('Отправка запроса на регистрацию'),
            stepTip: '',
            successText: this.$t('Запрос успешно отправлен на обработку.'),
            toNextStep: null,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          }
        ]
      },
      readonly () {
        return ((!!this.draft.organizationId && !this.canSendRequest) ||
          this.isRegistered) ||
          this.isLoading
      },
      canSendRequest () {
        const status = this.request.status
        return status === ORG_REQUEST_STATUSES.CREATED ||
          status === ORG_REQUEST_STATUSES.REJECTED ||
          !status
      }
    },
    watch: {
      requestType: {
        immediate: true,
        handler (newVal, oldVal) {
          if (newVal && newVal !== oldVal) {
            this.getRequestFiles()
          }
        }
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.initialize()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(ORGANIZATION_DRAFT.NAMESPACE, {
        mergeFieldsAction: ORGANIZATION_DRAFT.ACTIONS.MERGE_FIELDS,
        getOrganizationDraftAction: ORGANIZATION_DRAFT.ACTIONS.GET_ORGANIZATION_DRAFT,
        updateOrganizationDraftAction: ORGANIZATION_DRAFT.ACTIONS.UPDATE_ORGANIZATION_DRAFT,
        createOrganizationDraftAction: ORGANIZATION_DRAFT.ACTIONS.CREATE_ORGANIZATION_DRAFT,
        getRegistrationCardAction: ORGANIZATION_DRAFT.ACTIONS.GET_REGISTRATION_CARD,
        getOrganizationRegistrationRequestAction: ORGANIZATION_DRAFT.ACTIONS.GET_ORGANIZATION_REGISTRATION_REQUEST,
        sendOrganizationRegistrationRequestAction: ORGANIZATION_DRAFT.ACTIONS.SEND_ORGANIZATION_REGISTRATION_REQUEST,
        clearOrganizationDraftAction: ORGANIZATION_DRAFT.ACTIONS.CLEAR_ORGANIZATION_DRAFT,
        clearOrganizationRegistrationRequestAction: ORGANIZATION_DRAFT.ACTIONS.CLEAR_ORGANIZATION_REGISTRATION_REQUEST
      }),
      ...mapActions(BANK_ACCOUNTS_DRAFTS.NAMESPACE, {
        getBankAccountsDraftsAction: BANK_ACCOUNTS_DRAFTS.ACTIONS.GET_BANK_ACCOUNTS_DRAFTS,
        createBankAccountDraftAction: BANK_ACCOUNTS_DRAFTS.ACTIONS.CREATE_BANK_ACCOUNT_DRAFT,
        deleteBankAccountDraftAction: BANK_ACCOUNTS_DRAFTS.ACTIONS.DELETE_BANK_ACCOUNT_DRAFT,
        clearBankAccountsDraftsAction: BANK_ACCOUNTS_DRAFTS.ACTIONS.CLEAR_BANK_ACCOUNTS_DRAFTS
      }),
      ...mapActions(IDENTITY_FILES.NAMESPACE, {
        getRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.GET_REQUIRED_DOCUMENT_TYPES,
        getRequestFilesAction: IDENTITY_FILES.ACTIONS.GET_REQUEST_FILES,
        linkRequestFileAction: IDENTITY_FILES.ACTIONS.LINK_REQUEST_FILE,
        clearRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.CLEAR_REQUIRED_DOCUMENT_TYPES,
        clearRequestFilesAction: IDENTITY_FILES.ACTIONS.CLEAR_REQUEST_FILES
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearOrganizationDraftAction(),
            this.clearOrganizationRegistrationRequestAction(),
            this.clearBankAccountsDraftsAction(),
            this.clearRequiredDocumentTypesAction(),
            this.clearRequestFilesAction()
          ])
      },
      initialize: async function () {
        try {
          // if (this.isAdmin) {
          //   return
          // }
          this.isLoading = true
          await this.getOrganizationDraftAction()
          if (this.draft.organizationId) {
            await Promise.all([
              this.getOrganizationRegistrationRequestAction(),
              this.getBankAccountsDraftsAction()
            ])
          }

          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getBankAccountsDrafts: async function () {
        try {
          if (this.isAdmin) {
            return
          }
          this.isLoading = true
          await this.getBankAccountsDraftsAction()
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      getRequestFiles: async function () {
        // if (this.isAdmin) {
        //   return
        // }
        const model = {
          requestType: this.requestType
        }

        if (this.draft.organizationId) {
          model.objectId = this.draft.organizationId
          await Promise.all([
            this.getRequiredDocumentTypesAction(model),
            this.getRequestFilesAction(model)
          ])
        } else {
          await this.getRequiredDocumentTypesAction(model)
        }
      },
      getRequestFilesWithLoading: async function () {
        try {
          this.isLoading = true
          await this.getRequestFiles()
        } finally {
          this.isLoading = false
        }
      },
      validateAndSaveOrganization: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }

          const result = await this.$refs.organizationInfo.validate()
          if (!result) {
            throw new Vue.errorsTypes.ValidationError()
          }

          if (this.draftChanged) {
            try {
              this.isLoading = true
              this.success = false

              if (this.draft.organizationId) {
                await this.updateOrganizationDraftAction(this.draft)
              } else {
                await this.createOrganizationDraftAction(this.draft)
              }

              this.success = true
            } finally {
              this.isLoading = false
            }
          }

          this.toNextStep(step)
        } catch (error) {
          this.throwIfUnknownError(error)
        }
      },
      validateBankAccounts: async function (step) {
        try {
          if (this.readonly) {
            this.getRequestFilesWithLoading()
            this.toNextStep(step)
            return
          }

          if (this.bankAccounts.length === 0) {
            throw new Vue.errorsTypes.ValidationError(this.$t('Добавьте информацию по банковскому счету для продолжения.'))
          }
          this.getRequestFilesWithLoading()
          this.toNextStep(step)
        } catch (error) {
          this.throwIfUnknownError(error)
          // если ошибка, дальше не переходим
        }
      },
      validateAndSyncRequestFiles: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }
          // проверяем данные документов
          await this.$refs.requestFiles.validate()
          // в случае успешной проверки синхронизируем с хранилищем $store
          await this.$refs.requestFiles.syncronizeWithStoreData()
          //  на следующий шаг
          this.toNextStep(step)
        } catch (error) {
          this.throwIfNotValidationError(error)
          // если ошибка, дальше не переходим
        }
      },
      syncRequestFiles: async function (step) {
        try {
          if (this.readonly) {
            this.toPrevStep(step)
            return
          }
          // синхронизируем с хранилищем $store,
          // если что-то поменяли и хотим вернуться на предыдущий шаг
          await this.$refs.requestFiles.syncronizeWithStoreData()

          //  на предыдущий шаг
          this.toPrevStep(step)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error)
          // если ошибка, дальше не переходим
        }
      },
      sendOrganizationRegistrationRequest: async function () {
        try {
          if (!this.requestFiles.length) {
            throw new Vue.errorsTypes.ValidationError(this.$t('Нет прикрепленных файлов для отправки запроса.'))
          }
          this.isLoading = true
          this.success = false
          await this.sendOrganizationRegistrationRequestAction(this.requestFiles)

          this.success = true
          await Promise.all(
            [
              this.getOrganizationRegistrationRequestAction(),
              this.getRequestFiles()
            ]
          )
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
          // для запрета перехода на следующий шаг пробрасываем ошибку
        }
      },
      deleteBankAccount: async function (model) {
        try {
          this.isLoading = true
          await this.deleteBankAccountDraftAction(model)
          await this.getBankAccountsDrafts()
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      createBankAccount: async function (value) {
        try {
          this.isLoading = true
          await this.createBankAccountDraftAction(value)
          await this.getBankAccountsDrafts()
        } finally {
          this.isLoading = false
        }
      },
      toNextStep: function (step) {
        this.success = false
        this.stepNumber = step + 1
      },
      toPrevStep: function (step) {
        this.success = false
        this.stepNumber = step - 1
      },
      exitFromWizard: function () {
        this.goToRoute(`${this.urlRootPath}/user/registration/start`)
      },
      uploadFile: async function (fileId) {
        // свяжем файл с запросом
        const params = {
          requestType: this.requestType,
          objectId: this.draft.organizationId,
          model: { fileId }
        }
        await this.linkRequestFileAction(params)
      },
      getRegistrationCard: async function () {
        try {
          this.isLoading = true
          await this.getRegistrationCardAction()
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      }
    }
  }
</script>
